import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getExtensionId } from '../config/extension';
import { isStagingOrLocalhost } from '../utils/environment';
import {
  AdminViewMode,
  clearAllAuthStorage,
  getAdminViewMode,
  getEffectiveUser,
  isRealSuperAdmin,
  setAdminViewMode
} from '../utils/authSession';
import { syncPolicyPreviewDomain } from '../utils/policyPreview';
import HeaderUtilityMenu from './HeaderUtilityMenu';

interface AdminHeaderProps {
  title: string;
  subtitle?: string;
  helperText?: string;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({ title, subtitle, helperText, menuOpen, setMenuOpen }) => {
  const navigate = useNavigate();
  const usePersistentSidebar = isStagingOrLocalhost();
  const [isSuperAdminUser, setIsSuperAdminUser] = React.useState(false);

  React.useEffect(() => {
    try {
      const user = getEffectiveUser();
      if (!user) return;
      setIsSuperAdminUser(Boolean(user?.isSuperAdmin));
    } catch {
      setIsSuperAdminUser(false);
    }
  }, []);

  const showAccountSwitch = isRealSuperAdmin();

  const handleAccountTypeChange = (mode: AdminViewMode) => {
    if (mode === getAdminViewMode()) return;
    setAdminViewMode(mode);
    if (mode === 'super') syncPolicyPreviewDomain('');
    const nextPath = mode === 'domain' ? '/admin/settings' : '/admin/users';
    window.location.hash = `#${nextPath}`;
    window.location.reload();
  };

  const handleSignOut = () => {
    if (!window.confirm('Are you sure you want to sign out?')) {
      return;
    }

    setMenuOpen(false);
    clearAllAuthStorage();

    const chromeApi = (window as any).chrome;
    if (chromeApi?.runtime?.sendMessage) {
      const message = { action: 'CLEAR_ALL_DATA' };
      const extensionId = getExtensionId();
      try {
        if (extensionId) {
          chromeApi.runtime.sendMessage(extensionId, message, () => {});
        } else {
          chromeApi.runtime.sendMessage(message, () => {});
        }
      } catch (error) {
        console.error('Sign-out storage clear failed:', error);
      }
    }

    window.location.href = `${window.location.origin}${window.location.pathname}#/signout-confirmation`;
  };

  return (
    <div className="mb-8 relative">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {!usePersistentSidebar && (
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex flex-col gap-1.5 p-2.5 hover:bg-gray-100 rounded-lg transition-colors"
              style={{
                minWidth: '44px',
                minHeight: '44px',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Menu"
            >
              <div
                className="rounded-sm"
                style={{ width: '28px', height: '4px', backgroundColor: '#212529' }}
              />
              <div
                className="rounded-sm"
                style={{ width: '28px', height: '4px', backgroundColor: '#212529' }}
              />
              <div
                className="rounded-sm"
                style={{ width: '28px', height: '4px', backgroundColor: '#212529' }}
              />
            </button>
          )}

          {/* Traffic Lite Logo */}
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 -mt-1">
            <img src="/icon.png" alt="Traffic Lite" className="w-10 h-10 object-contain" />
          </div>

          <div>
            <h1
              className="traffic-heading-font text-[30px] font-bold"
              style={{ color: '#212529', marginBottom: subtitle ? '4px' : 0 }}
            >
              {title}
            </h1>
            {subtitle && (
              <p className="traffic-heading-font text-xl font-bold" style={{ color: '#374151' }}>
                {subtitle}
              </p>
            )}
            {helperText && (
              <p className="traffic-heading-font text-base" style={{ color: '#6b7280', marginTop: '4px' }}>
                {helperText}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {showAccountSwitch && (
            <select
              aria-label="Account type"
              value={getAdminViewMode()}
              onChange={(event) => handleAccountTypeChange(event.target.value as AdminViewMode)}
              className="traffic-menu-font"
              style={{
                height: '42px',
                borderRadius: '9999px',
                border: '1px solid #d1d5db',
                backgroundColor: '#ffffff',
                padding: '0 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#212529',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <option value="super">Super Admin</option>
              <option value="domain">Domain Admin</option>
            </select>
          )}
          {!isSuperAdminUser && <HeaderUtilityMenu onSignOut={handleSignOut} />}
        </div>
      </div>

      {/* Dropdown Menu */}
      {!usePersistentSidebar && menuOpen && (
        <div
          className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[240px] z-[1000]"
          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
        >
          {isSuperAdminUser ? (
            <>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/users');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors rounded-t-lg"
                style={{ color: '#212529' }}
              >
                Users
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/domains');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
                style={{ color: '#212529' }}
              >
                Domains
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
                style={{ color: '#212529' }}
              >
                Data Dashboard
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/settings');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
                style={{ color: '#212529' }}
              >
                Policy Templates
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/settings');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors rounded-t-lg"
                style={{ color: '#212529' }}
              >
                Policy Details
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/users');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
                style={{ color: '#212529' }}
              >
                Users
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin/groups');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
                style={{ color: '#212529' }}
              >
                Groups
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/admin');
                }}
                className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors rounded-b-lg"
                style={{ color: '#212529' }}
              >
                Data Dashboard
              </button>
            </>
          )}
          {isSuperAdminUser ? (
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate('/admin/feedback');
              }}
              className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-gray-100 transition-colors"
              style={{ color: '#212529' }}
            >
              Feedback
            </button>
          ) : null}
          {isSuperAdminUser && (
            <button
              onClick={handleSignOut}
              className="traffic-menu-font w-full text-left px-4 py-3 text-[15px] font-medium hover:bg-red-50 transition-colors rounded-b-lg"
              style={{ color: '#b91c1c' }}
            >
              Sign Out
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminHeader;
